"use client";

import { formatter } from "@/app/checkout/(squeeze)/review/checkoutReviewPage";
import fixImageURI from "@/app/ec/product/fixImageURI";
import ProductImage from "@/app/ec/product/productImage";
import { useShowLicenseRequiredBadge } from "@/app/hooks/useLicenseRequiredBadge";
import Badge from "@/components/design-system/badge";
import LicenseRequiredBadge from "@/components/license-required-badge/licenseRequiredBadge";
import { isExpirationDescription } from "@/utils/promoDescriptions";
import Link from "next/link";
import WarningIcon from "public/next-public/common/icons/warning-icon.svg";
import { useState } from "react";
import styles from "./checkoutReviewVendorOrderItemSection.module.scss";

export default function CheckoutReviewVendorOrderItemSection({
  product,
  isConfirmationPage,
}: CheckoutReviewVendorOrderItemSectionProps) {
  const [showFullText, setShowFullText] = useState(false);
  const hasMoreToggle = product?.productDescription?.length > 100;

  const promoDescription = product?.promoAdditionalDescription ?? "";
  const isExpiration = isExpirationDescription(promoDescription);
  const expirationLabel = isExpiration ? promoDescription : "";
  const otherPromoDescription = isExpiration ? "" : promoDescription;

  const showLicenseRequiredBadge = useShowLicenseRequiredBadge(Boolean(product?.licenseRequired));
  const hasBadges = showLicenseRequiredBadge || Boolean(product && expirationLabel);
  const hasWarnings = Boolean(
    product &&
    (otherPromoDescription ||
      product.isBackordered ||
      (product.fulfillmentPolicyVendorVO?.derivedHandlingShipTime ?? 0) >= 6 ||
      product.hasShortExpirationDate),
  );

  // Coerced up front: a bare `savingsOverRetail &&` guard renders a literal "0" in JSX.
  const savingsOverRetail = product?.savingsOverRetail ?? 0;

  return (
    <div className={styles.VendorOrderItem}>
      <div className={styles.row}>
        <div className={styles.imageContainer}>
          <Link href={product?.productLink} title={product?.productTitle}>
            <ProductImage
              src={fixImageURI(product?.smallImagePath ?? "") || undefined}
              className={styles.image}
              alt={product?.productTitle}
              width="90"
              height="90"
            />
          </Link>
        </div>

        <div className={styles.infoRow}>
          <div className={styles.titleBlock}>
            {hasBadges && (
              <div className={styles.badges}>
                <LicenseRequiredBadge licenseIsRequired={Boolean(product?.licenseRequired)} />
                {expirationLabel && (
                  <Badge
                    label={expirationLabel}
                    color="red"
                    size="small"
                    icon={<WarningIcon width={12} height={12} aria-hidden />}
                  />
                )}
              </div>
            )}
            <h3 className={`${styles.title} ${hasMoreToggle ? styles.hasMoreLink : ""}`}>
              <Link href={product?.productLink} className={styles.titleAnchor}>
                {product?.productTitle}
              </Link>
            </h3>
            <div className={styles.description}>
              <span className={`${styles.descText} ${showFullText ? "" : styles.clamp}`}>
                {product?.productDescription}
              </span>
              {hasMoreToggle && (
                <button type="button" className={styles.moreLink} onClick={() => setShowFullText(!showFullText)}>
                  {showFullText ? "Less" : "More"}
                </button>
              )}
            </div>
          </div>

          {hasWarnings && (
            <div className={styles.warnings}>
              {otherPromoDescription && <p className={styles.promoDescription}>{otherPromoDescription}</p>}

              {product.isBackordered && <p className={styles.warning}>Backordered.</p>}
              {(product.fulfillmentPolicyVendorVO?.derivedHandlingShipTime ?? 0) >= 6 && (
                <p className={styles.warning}>Long Handling Time.</p>
              )}
              {(product.isBackordered || (product.fulfillmentPolicyVendorVO?.derivedHandlingShipTime ?? 0) >= 30) && (
                <p className={styles.warning}>Shipping may be delayed.</p>
              )}
              {/* TODO(SPR-2592): backend to populate `hasShortExpirationDate`; copy pending content sign-off. */}
              {product.hasShortExpirationDate && <p className={styles.warning}>Short expiration date.</p>}
            </div>
          )}

          <div className={styles.priceContainer}>
            <span className={styles.unitPrice}>
              {product?.unitPrice ? formatter.format(product?.unitPrice) : "$0.00"}
            </span>
            {savingsOverRetail > 0 && (
              <div className={styles.savings}>
                You Saved: ${savingsOverRetail.toFixed(2)}
                <span className={styles.savingsPercentage}>{product?.savingsOverRetailPercentage}%</span>
              </div>
            )}
          </div>

          {product?.quantity >= 1 && (
            <div className={styles.quantityContainer}>
              <span>
                {product?.quantity}&nbsp;{product?.quantity > 1 ? "Items" : "Item"}
              </span>
              {!isConfirmationPage && (
                <Link className={styles.editLink} href="/shopping-cart">
                  Edit
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export type CheckoutReviewVendorOrderItemSectionProps = {
  product: ShoppingCartVendorOrderProduct;
  isConfirmationPage?: boolean;
};
